/**
 * States Editing mode — the editor-wide on/off switch for authoring Properties
 * state groups in the viewport.
 *
 * Always starts OFF on load (not persisted): a Properties behavior on the
 * selection only surfaces a compact "Edit states" button (mount-state-context.tsx)
 * until the user opts in for the session. Turning it ON shows the full state strip
 * AND engages capture routing — transform/value/active edits store into the active
 * state instead of the authored/base scene (state-capture.ts reads
 * {@link isStatesEditingEnabled} to gate every capture path).
 *
 * The flag lives here (not in mount-state-context) so the capture path can read
 * it without importing the React/mount module, avoiding an import cycle.
 */

// Session-only; deliberately NOT persisted — every page load starts collapsed.
let enabled = false;

const listeners = new Set<(enabled: boolean) => void>();

/** Whether States Editing mode is currently on. */
export function isStatesEditingEnabled(): boolean {
    return enabled;
}

/** Flip States Editing mode and notify listeners (session-only, not persisted). */
export function setStatesEditingEnabled(next: boolean): void {
    if (enabled === next) return;
    enabled = next;
    for (const cb of listeners) cb(next);
}

/** Subscribe to mode changes; returns an unsubscribe. */
export function onStatesEditingChanged(cb: (enabled: boolean) => void): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
}
